/* so-sheet-swipe.js — swipe down to close, shared by every so-* bottom sheet (Task 23, 2026-09-28).
   Loaded once site-wide (deferred) from snippets/so-postcode.liquid. One API:

     window.soSheetSwipe.attach(panel, { handle, handleOnly, scroller, backdrop, onClose, isOpen, media })
       panel     the element that slides (it must animate its open/close with a CSS transform transition)
       handle    element | selector | function — a downward drag starting here always moves the panel
                 (grab bar, header). Areas that don't scroll (header, footer, padding) drag too.
       scroller  element | selector | function — the sheet's list. A drag starting inside ANY scrolling
                 area moves the panel only when that area is at scrollTop 0 and the finger moves down;
                 otherwise the list scrolls as normal. (The nearest scrolling ancestor is found per
                 gesture, so re-rendered content and nested lists just work.)
       backdrop  element | selector | function — fades with the drag (optional)
       handleOnly  true → ONLY a drag that starts on `handle` moves the panel; the rest of the panel (its list,
                 buttons, padding) never does (Task 26: the vendor cart / quick-add drawers, whose item list
                 people scroll and tap +/− in)
       onClose   the sheet's OWN close function (focus return, scroll unlock, cleanup stay there)
       isOpen    function (optional) — default: the panel or an ancestor has .is-open
       media     media query for "this is a bottom sheet" — default phones, (max-width: 767.98px)
       extraHosts  element | selector | list | function (optional, Task 30) — overlays that belong with this sheet but
                 live outside it; touches in them are left alone (other open aria-modal dialogs / .so-modal already are)
     → returns { detach, update }. Attaching the same panel again just updates its options.

   Behaviour: the panel follows the finger (translateY), the backdrop fades; on release it closes when
   dragged past 30% of its height or flicked down faster than 0.5 px/ms, else it springs back via the
   panel's own CSS transition (none with prefers-reduced-motion). Touch and pen only — a mouse, or a
   desktop layout (centred modal / side drawer), is ignored. Horizontal gestures, taps, text fields and
   the focus trap are left alone; nothing tracks visualViewport. Listeners live on the panel, so a sheet
   that is removed from the page takes them with it; detach() is there for sheets that are kept.

   Task 30 (phones, while any attached sheet is open): a vertical touchmove never scrolls the page — it is
   cancelled unless a scroll container between the finger and the panel can still scroll that way (content that
   fits, a list at its top pulled down, at its bottom pushed up → cancelled; touches on the backdrop / page behind
   → always cancelled). Sideways gestures, pinch and touches that start on a text field are left alone. Panels get
   overscroll-behavior-y: contain on every descendant, and <html> gets .so-sheet-open + --so-sheet-bg (the
   last-opened sheet's colour) so Safari's under-toolbar area matches the sheet. No overflow lock / scroll jump here. */
(function () {
  'use strict';
  var CLOSE_FRACTION = 0.3;   // of the panel's height
  var CLOSE_VELOCITY = 0.5;   // px/ms, downward
  var SLOP = 6;               // px before a gesture counts as a drag

  // Pure decisions (node tests: scripts/tests/so-sheet-swipe.test.cjs in the smoke-organic repo).
  // dy = how far the panel was dragged down (px), height = the panel's height, velocity = px/ms at release (down > 0).
  function shouldClose(dy, height, velocity) {
    if (!(dy > 0)) return false;
    if (velocity < -CLOSE_VELOCITY) return false;           // flicked back up: stay open
    if (velocity > CLOSE_VELOCITY) return true;
    return height > 0 && dy >= height * CLOSE_FRACTION;
  }
  // A move inside a scrolling list drags the sheet only when the list is at its top and the move is downward.
  function shouldDragFromScroller(scrollTop, dy) { return scrollTop <= 0 && dy > 0; }

  // Task 30 — scroll containment. m = one scroll container's { scrollTop, scrollHeight, clientHeight } plus
  // dy = this touchmove's vertical finger movement (px, down > 0). True when that container can NOT scroll any
  // further that way — the move would chain to the page (or fire Safari's pull-to-refresh). No container → true.
  function shouldPreventScroll(m) {
    if (!m) return true;
    var dy = m.dy;
    if (!dy) return false;                                   // no vertical movement: nothing to chain
    // Judged on the live position, never on "fits": a list that shrank under a scrolled position (an item removed
    // while scrolled down; iOS doesn't always clamp) must still scroll back up, or the sheet looks frozen.
    if (dy > 0) return !(m.scrollTop > 0);                   // finger down = towards the top: blocked only AT the top
    return !(m.scrollTop + (m.clientHeight || 0) < (m.scrollHeight || 0) - 1);   // finger up: blocked only at the bottom
  }
  // One touchmove while a sheet is open on a phone. t = { inPanel, field, horizontal, dy, scrollers: [metrics…] }:
  //   inPanel     the touch started inside an open sheet (else it's the backdrop / page behind: always blocked)
  //   field       it started on a text field / select (caret, selection, the picker keep their own gestures)
  //   horizontal  the gesture is sideways (chip rows etc. still scroll)
  //   scrollers   every vertical scroll container between the touch and the panel (innermost first)
  // Blocked when none of them can scroll further in the finger's direction.
  //   foreign     it started inside some OTHER open dialog / modal on top (not one of ours): never touched
  function shouldPreventTouch(t) {
    if (t && t.foreign) return false;
    if (!t || !t.inPanel) return true;
    if (t.field || t.horizontal) return false;
    var list = t.scrollers || [];
    for (var i = 0; i < list.length; i++) {
      var m = list[i];
      if (!shouldPreventScroll({ scrollTop: m.scrollTop, scrollHeight: m.scrollHeight, clientHeight: m.clientHeight, dy: t.dy })) return false;
    }
    return true;
  }

  // Direction of a touch gesture from its total travel (px): 'x' | 'y' (ties → 'y'), null before it has moved;
  // locked = it has travelled SLOP px, so the direction no longer changes for the rest of the gesture.
  function gestureAxis(ax, ay) {
    ax = Math.abs(ax) || 0; ay = Math.abs(ay) || 0;
    if (!ax && !ay) return { axis: null, locked: false };
    return { axis: ax > ay ? 'x' : 'y', locked: Math.max(ax, ay) >= SLOP };
  }

  var pure = { gestureAxis: gestureAxis, SLOP: SLOP, shouldClose: shouldClose, shouldDragFromScroller: shouldDragFromScroller, shouldPreventScroll: shouldPreventScroll, shouldPreventTouch: shouldPreventTouch, CLOSE_FRACTION: CLOSE_FRACTION, CLOSE_VELOCITY: CLOSE_VELOCITY };
  if (typeof module === 'object' && module.exports) module.exports = pure;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.soSheetSwipe) return;                           // loaded twice: keep the first

  function mq(q) { try { return !!(window.matchMedia && window.matchMedia(q).matches); } catch (e) { return false; } }
  function reduced() { return mq('(prefers-reduced-motion: reduce)'); }
  var NO_DRAG = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

  // Bleed (phones): iOS Safari leaves a strip under a bottom sheet — behind its toolbar, and when the keyboard
  // scrolls the page — where the white page or its content shows through. Each sheet and its backdrop paint
  // their own colour 100vh further down via an extra box-shadow (a shadow can't add scroll height the way a
  // pseudo-element would inside a scrolling panel). CSS applies it on phones only, so desktop modals are untouched.
  // Task 30 (same style block, phones only): while a sheet is open the page root takes the sheet's colour (Safari
  // fills the area under its floating toolbar from the document background, which the bleed shadow doesn't reach)
  // and never rubber-bands / pull-to-refreshes; every scroll container inside a sheet keeps its overscroll.
  // Rounded corners (2026-09-29, ux/redesign-2026-09/modals): the bleed starts 24px below the panel's top edge
  // (y-offset 50vh + 24px against a 50vh spread), so it never fills the top-corner cut-outs — the dimmed page shows
  // in the corners. 24px clears every sheet radius (dsheet 18, .so-vd 16, cart add-ons 14, vendor drawers 4).
  var BLEED_CSS = '@media (max-width: 767.98px){.so-bleed{box-shadow:var(--so-bleed-orig,0 0 #0000),0 calc(50vh + 24px) 0 50vh var(--so-bleed)!important}' +
    // Only the canvas (<html>) takes the sheet colour. Painting <body> too showed through the transparent <main>
    // and gaps between sections under the backdrop, while the header (own white background) did not: two tones
    // behind the dim (Charl 2026-09-29). The page stays white; the panel's own bleed covers Safari's toolbar area.
    'html.so-sheet-open{background-color:var(--so-sheet-bg)!important}' +
    'html.so-sheet-open,html.so-sheet-open body{overscroll-behavior-y:none}' +
    '.so-sheet-contain,.so-sheet-contain *{overscroll-behavior-y:contain}}';
  function bleedStyle() {
    if (typeof document === 'undefined' || document.getElementById('so-sheet-bleed-css')) return;
    var st = document.createElement('style'); st.id = 'so-sheet-bleed-css'; st.textContent = BLEED_CSS;
    (document.head || document.documentElement).appendChild(st);
  }
  function bleed(el) {
    if (!el || el.nodeType !== 1 || el.__soBleed || typeof getComputedStyle !== 'function') return;
    var cs = getComputedStyle(el), bg = cs.backgroundColor;
    if (!opaque(bg)) return;                                 // nothing to extend
    bleedStyle();
    el.__soBleed = true;
    if (cs.boxShadow && cs.boxShadow !== 'none') el.style.setProperty('--so-bleed-orig', cs.boxShadow);
    el.style.setProperty('--so-bleed', bg);
    el.classList.add('so-bleed');
  }

  function opaque(bg) { return !!bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg); }

  // ---- Task 30: the open-sheet registry (scroll containment + root background) --------------------------------
  // Every attached panel is registered with its "open on a phone" test. The last one opened wins the root colour.
  var reg = [], openSeq = 0, paintTimer = 0;
  var FIELD = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
  function openSheets() {
    var out = [];
    for (var i = reg.length - 1; i >= 0; i--) {
      var r = reg[i];
      if (!r.panel.isConnected) { reg.splice(i, 1); schedulePaint(); continue; }  // removed sheet: forget it, repaint the root
      var on = false;
      try { on = r.active(); } catch (e) {}
      if (on && !r.on) r.at = ++openSeq;
      r.on = on;
      if (on) out.push(r);
    }
    return out;
  }
  function paintRoot() {
    paintTimer = 0;
    var open = openSheets(), top = null;
    open.forEach(function (r) { if (!top || r.at > top.at) top = r; });
    var html = document.documentElement;
    var bg = top ? getComputedStyle(top.panel).backgroundColor : '';
    if (top && opaque(bg)) {
      bleedStyle();
      html.style.setProperty('--so-sheet-bg', bg);
      html.classList.add('so-sheet-open');                 // background + overscroll only: no layout change, no scroll jump
    } else if (html.classList.contains('so-sheet-open')) {
      html.classList.remove('so-sheet-open');
      html.style.removeProperty('--so-sheet-bg');
    }
    watchRemovals(open.length > 0);
  }
  function schedulePaint() { if (!paintTimer) paintTimer = setTimeout(paintRoot, 0); }   // coalesces a burst of class changes (not rAF: paused in hidden tabs)
  function register(panel, active, extra) {
    for (var i = 0; i < reg.length; i++) if (reg[i].panel === panel) { reg[i].active = active; reg[i].extra = extra; schedulePaint(); return; }
    reg.push({ panel: panel, active: active, extra: extra, on: false, at: 0 });
    bleedStyle();
    panel.classList.add('so-sheet-contain');
    if (!watching) watch();
    schedulePaint();
  }
  function unregister(panel) {
    for (var i = 0; i < reg.length; i++) if (reg[i].panel === panel) { reg.splice(i, 1); break; }
    schedulePaint();
  }
  // Sheets open/close by a class or the `open` attribute on themselves or an ancestor — or are simply removed
  // from the page (childList), which must drop .so-sheet-open too. Idle when nothing is registered or painted.
  // Two observers: open/close (a class or attribute change) is watched always — the browser filters those for us;
  // node removal (childList, which it can't filter) is watched ONLY while a sheet is open, so an ordinary page
  // pays nothing for every carousel or widget that adds and removes nodes (mobile INP).
  var watching = false, removals = null;
  function watch() {
    watching = true;
    try {
      new MutationObserver(function () {
        if (reg.length || document.documentElement.classList.contains('so-sheet-open')) schedulePaint();
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'open', 'hidden'], subtree: true });
    } catch (e) {}
  }
  function watchRemovals(on) {
    try {
      if (on && !removals) {
        removals = new MutationObserver(function (ms) {
          for (var i = 0; i < ms.length; i++) if (ms[i].removedNodes.length) { schedulePaint(); return; }
        });
        removals.observe(document.body || document.documentElement, { childList: true, subtree: true });
      } else if (!on && removals) { removals.disconnect(); removals = null; }
    } catch (e) {}
  }

  // A touch inside another open dialog layered over our sheet (e.g. so-cart-delivery's "How delivery is priced"
  // .so-modal, appended to <body>) belongs to that dialog: we leave it entirely alone. Ours are excluded — a
  // dialog that holds one of our panels (so-postcode's aria-modal .so-dsheet root) is still ours.
  var FOREIGN = '[aria-modal="true"], .so-modal:not([hidden]), dialog[open]';
  function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
  function inForeign(t) {
    for (var el = t && t.closest ? t.closest(FOREIGN) : null; el; el = el.parentElement && el.parentElement.closest(FOREIGN)) {
      var ours = false;
      for (var i = 0; i < reg.length; i++) if (el === reg[i].panel || el.contains(reg[i].panel)) { ours = true; break; }
      if (!ours && visible(el)) return true;
    }
    for (var j = 0; j < reg.length; j++) {                   // attach(…, { extraHosts }) — the sheet's own add-ons
      var x = reg[j].extra;
      if (!x) continue;
      try {
        if (typeof x === 'function') x = x();
        var list = typeof x === 'string' ? document.querySelectorAll(x) : (x.length != null ? x : [x]);
        for (var k = 0; k < list.length; k++) if (list[k] && list[k].contains && list[k].contains(t)) return true;
      } catch (e) {}
    }
    return false;
  }

  // Touches: decided per gesture at touchstart (which sheets are open, which one holds the finger), then per move.
  var tg = null;
  function vScrollers(t, panel) {
    var out = [];
    for (var el = t; el && el.nodeType === 1; el = el.parentElement) {
      var oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') out.push(el);
      if (el === panel) break;
    }
    return out;
  }
  function onTouchStart(e) {
    tg = null;
    if (!reg.length || !e.touches || e.touches.length !== 1) return;
    var open = openSheets();
    if (!open.length) return;
    var t = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
    var host = null;                                         // the innermost open sheet holding the touch
    open.forEach(function (r) { if (t && r.panel.contains(t) && (!host || host.contains(r.panel))) host = r.panel; });
    if (!host && t && inForeign(t)) return;                  // another dialog on top owns this touch
    var tp = e.touches[0];
    tg = { host: host, t: t, x0: tp.clientX, y0: tp.clientY, y: tp.clientY, axis: null,
           field: !!(t && t.closest && t.closest(FIELD)) };
  }
  function onTouchMove(e) {
    if (!tg) return;
    var tp = e.touches && e.touches[0];
    if (!tp || e.touches.length > 1) { tg = null; return; }   // pinch-zoom: leave it alone
    var dy = tp.clientY - tg.y;
    tg.y = tp.clientY;
    // The direction locks once the finger has moved SLOP px; until then each move is judged on its own.
    var axis = tg.axis;
    if (!axis) {
      var g = gestureAxis(tp.clientX - tg.x0, tp.clientY - tg.y0);
      if (!g.axis) return;
      axis = g.axis;
      if (g.locked) tg.axis = axis;
    }
    if (!e.cancelable) return;                              // the browser already owns this gesture
    var scrollers = tg.host && !tg.field && axis === 'y' ? vScrollers(tg.t, tg.host).map(function (el) {
      return { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight, clientHeight: el.clientHeight };
    }) : [];
    if (shouldPreventTouch({ inPanel: !!tg.host, field: tg.field, horizontal: axis === 'x', dy: dy, scrollers: scrollers })) e.preventDefault();
  }
  function onTouchEnd() { tg = null; }
  document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
  document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
  document.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
  document.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });

  function attach(panel, opts) {
    if (!panel || panel.nodeType !== 1) return null;
    if (panel.__soSwipe) { panel.__soSwipe.update(opts); return panel.__soSwipe; }
    var o = opts || {};
    var st = null;                                           // the gesture in progress

    function res(x, list) {                                  // element | selector (inside the panel) | function
      if (!x) return list ? [] : null;
      if (typeof x === 'function') x = x();
      if (typeof x === 'string') return list ? Array.prototype.slice.call(panel.querySelectorAll(x)) : panel.querySelector(x);
      return list ? [x] : x;
    }
    function backdrop() { var b = o.backdrop; if (typeof b === 'function') b = b(); if (typeof b === 'string') b = document.querySelector(b); return b || null; }
    function openOnPhone() {
      if (!mq(o.media || '(max-width: 767.98px)')) return false;
      return typeof o.isOpen === 'function' ? !!o.isOpen() : !!(panel.classList.contains('is-open') || (panel.closest && panel.closest('.is-open')));
    }
    function enabled() {
      var ae = document.activeElement;                       // typing in the sheet: a drag scrolls, it never closes
      if (ae && panel.contains(ae) && ae.matches && ae.matches(NO_DRAG)) return false;
      return openOnPhone();
    }
    function onHandle(t) { return (!o.handleOnly && t === panel) || res(o.handle, true).some(function (h) { return h && h.contains(t); }); }
    // The nearest ancestor (inside the panel) that can scroll vertically, else the given scroller if it holds the target.
    function scrollerOf(t) {
      for (var el = t; el && el !== panel && el.nodeType === 1; el = el.parentElement) {
        var oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      }
      var s = res(o.scroller);
      return s && s.contains(t) ? s : null;
    }

    function down(e) {
      if (st || e.pointerType === 'mouse' || e.isPrimary === false || !enabled()) return;
      var t = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
      if (!t || (t.closest && t.closest(NO_DRAG))) return;   // text fields keep their own gestures (caret, selection)
      var onH = onHandle(t);
      if (o.handleOnly && !onH) return;                     // handle-only sheets: the list and everything else stay put
      st = { id: e.pointerId, x0: e.clientX, y0: e.clientY, handle: onH, scroller: null, drag: false, pts: [] };
      if (!st.handle) st.scroller = scrollerOf(t);
    }
    // Decides, for the first moves of a gesture, whether this is a sheet drag. null = not decided yet.
    function decide(dx, dy) {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return null;
      if (Math.abs(dx) > Math.abs(dy) || dy <= 0) return false;       // sideways or upward: not ours
      if (!st.handle && st.scroller && !shouldDragFromScroller(st.scroller.scrollTop, dy)) return false;
      return true;
    }
    function begin(dy) {
      st.drag = true;
      st.from = dy;                                          // the panel starts following from here (no jump)
      st.h = panel.getBoundingClientRect().height;
      st.bd = backdrop();
      try { panel.setPointerCapture(st.id); } catch (err) { /* already released */ }
      panel.style.transition = 'none';
      if (st.bd) st.bd.style.transition = 'none';
      panel.classList.add('is-dragging');
    }
    function move(e) {
      if (!st || e.pointerId !== st.id) return;
      var dx = e.clientX - st.x0, dy = e.clientY - st.y0;
      if (!st.drag) {
        var d = decide(dx, dy);
        if (d === false) { st = null; return; }
        if (d === null) return;
        begin(dy);
      }
      var off = Math.max(0, dy - st.from);
      panel.style.transform = 'translate3d(0,' + off + 'px,0)';
      if (st.bd && st.h) st.bd.style.opacity = String(Math.max(0, 1 - off / st.h));
      st.off = off;
      st.pts.push([e.timeStamp || Date.now(), off]);
      if (st.pts.length > 8) st.pts.shift();
    }
    // Touch: keep the browser from scrolling (or rubber-banding, or chaining to the page) while the sheet
    // follows the finger — and, before the gesture is decided, while it can only become a sheet drag.
    function touchmove(e) {
      if (!st || !e.cancelable) return;
      if (st.drag) { e.preventDefault(); return; }
      var tp = e.touches && e.touches[0];
      if (!tp || e.touches.length > 1) return;
      var dx = tp.clientX - st.x0, dy = tp.clientY - st.y0;
      if (dy > 0 && Math.abs(dy) >= Math.abs(dx) && (st.handle || !st.scroller || shouldDragFromScroller(st.scroller.scrollTop, dy))) e.preventDefault();
    }
    function velocity(g) {
      var p = g.pts, n = p.length;
      if (n < 2) return 0;
      var last = p[n - 1], i = n - 2;
      while (i > 0 && last[0] - p[i][0] < 80) i--;             // measure over roughly the last 80 ms
      var dt = last[0] - p[i][0];
      return dt > 0 ? (last[1] - p[i][1]) / dt : 0;
    }
    function up(e) {
      if (!st || e.pointerId !== st.id) return;
      var g = st; st = null;
      if (!g.drag) return;
      var close = e.type === 'pointerup' && shouldClose(g.off || 0, g.h, velocity(g));
      try { panel.releasePointerCapture(g.id); } catch (err) { /* gone */ }
      panel.classList.remove('is-dragging');
      suppressClick();
      if (close && typeof o.onClose === 'function') o.onClose();
      settle(g.bd);
    }
    // Hand the panel back to its CSS: the transition (restored in the same frame as the transform) animates
    // from where the finger left it to open (spring back) or to closed (the close function removed .is-open).
    function settle(bd) {
      var rm = reduced();
      panel.style.transition = rm ? 'none' : '';
      panel.style.transform = '';
      if (bd) { bd.style.transition = rm ? 'none' : ''; bd.style.opacity = ''; }
      if (rm) setTimeout(function () { panel.style.transition = ''; if (bd) bd.style.transition = ''; }, 50);
    }
    // The click that can follow a drag's pointerup (a drag that started on a row or button) must not act.
    function suppressClick() {
      var until = Date.now() + 400;
      function kill(ev) { if (Date.now() < until && panel.contains(ev.target)) { ev.preventDefault(); ev.stopPropagation(); } window.removeEventListener('click', kill, true); }
      window.addEventListener('click', kill, true);
      setTimeout(function () { window.removeEventListener('click', kill, true); }, 450);
    }

    panel.addEventListener('pointerdown', down);
    panel.addEventListener('pointermove', move);
    panel.addEventListener('pointerup', up);
    panel.addEventListener('pointercancel', up);
    panel.addEventListener('touchmove', touchmove, { passive: false });

    function bleedAll() { try { bleed(panel); bleed(backdrop()); } catch (e) {} }
    bleedAll();
    register(panel, openOnPhone, o.extraHosts);

    var api = {
      update: function (next) { if (next) o = next; bleedAll(); register(panel, openOnPhone, o.extraHosts); },
      detach: function () {
        panel.removeEventListener('pointerdown', down);
        panel.removeEventListener('pointermove', move);
        panel.removeEventListener('pointerup', up);
        panel.removeEventListener('pointercancel', up);
        panel.removeEventListener('touchmove', touchmove, { passive: false });
        if (st && st.drag) { panel.classList.remove('is-dragging'); settle(st.bd); }
        st = null;
        unregister(panel);
        delete panel.__soSwipe;
      }
    };
    panel.__soSwipe = api;
    return api;
  }

  window.soSheetSwipe = { attach: attach, shouldClose: shouldClose, shouldDragFromScroller: shouldDragFromScroller, shouldPreventScroll: shouldPreventScroll, shouldPreventTouch: shouldPreventTouch };
})();
